/**
 * @group Utilities
 */
export const makeLogger =
  (isProdEnv: boolean) =>
  <T>(x: T, ...y: unknown[]) => {
    if (!isProdEnv) console.log(x, ...y)
    return x
  }

/**
 * @group Utilities
 */
export const makeErrorLogger =
  (isProdEnv: boolean) =>
  <T>(x: T, ...y: unknown[]) => {
    if (!isProdEnv) console.error(x, ...y)
    return x
  }

/**
 * @group Utilities
 * @description
 * A slightly opinionated wrapper around `console.assert` that returns the datum passed in.
 * It's useful for chaining assertions in a pipeline or method chain.
 *
 * Note that this does not throw an error, it just logs it to the console.
 *
 * @example
 * ```ts
 * const pipedAssert = makePipedAssertionLogger(true)
 * const asyncResult =
 *   await fetch('https://example.com')
 *      .then(res => res.json())
 *      .then(pipedAssert((data) => data.status === 'ok', 'Expected status to be ok'))
 *      .then(data => data.data)
 * ```
 */
export const makePipedAssertionLogger = (shouldLog: boolean) =>
  shouldLog
    ? <T>(test: (val: T) => boolean, msg: string) =>
        (datum: T, ...otherData: unknown[]) => {
          console.assert(test(datum), msg, datum, ...otherData)
          return datum
        }
    : <T>() =>
        (d: T) =>
          d

/**
 * @group Utilities
 * @description a wrapper around console.assert that returns the datum
 */
export const makeAssertionLogger = (shouldLog: boolean) =>
  shouldLog
    ? <T>(cond: boolean, msg: string, datum?: T, ...otherData: unknown[]) => {
        console.assert(cond, msg, datum, ...otherData)
        return datum
      }
    : <T>(_cond: boolean, _msg: string, datum?: T) => datum

type LoggerSet = {
  log: <Data>(x: Data, ...rest: unknown[]) => Data
  logError: <Data>(x: Data, ...rest: unknown[]) => Data
  assert: <Data>(
    condition: boolean,
    message: string,
    datum?: Data,
    ...otherData: unknown[]
  ) => Data | undefined
  pipedAssert: <Data>(
    test: (datum: Data) => boolean,
    message: string
  ) => (datum: Data, ...rest: unknown[]) => Data
}

/**
 * @group Utilities
 *
 * @description
 * creates a set of pipeable loggers that can be used to log data in a pipeline/chain
 */
export const makeLoggers = (shouldLog: boolean): LoggerSet => ({
  log: makeLogger(!shouldLog),
  logError: makeErrorLogger(!shouldLog),
  assert: makeAssertionLogger(shouldLog),
  pipedAssert: makePipedAssertionLogger(shouldLog)
})
